import React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import useCheckoutState from './useCheckoutState';
import trackEvent from '../lib/eventTracker';
import { PAGE_TYPES } from '../enums/page-types';
import { injectTrackingScript } from '../utils/scriptInjector';
import logException from '../services/logException';

const EventTrackingContext = createContext();

export const useEventTracking = () => useContext(EventTrackingContext);

export const EventTrackingProvider = ({ children }) => {
  const { checkout, trackedEvents, setTrackedEvents, iframeId } = useCheckoutState();
  const [shouldTrackEvents, setShouldTrackEvents] = useState(false);
  const [shouldTrackEnterEvent, setShouldTrackEnterEvent] = useState(false);

  // load tracking codes
  useEffect(() => {
    if (!checkout?._id) return;

    if (!checkout?.trackingCodes) {
      setShouldTrackEvents(true);
    } else {
      injectTrackingScript(checkout.trackingCodes)
        .then(() => {
          setShouldTrackEvents(true);
        })
        .catch(error => {
          logException(error);
        });
    }
  }, [checkout?._id, checkout?.trackingCodes]);

  function handleTrackEvent(event, properties) {
    trackEvent(event, properties, checkout._id, {
      pageslug: checkout.slug,
      sellerUrlId: checkout.seller.name || checkout.seller._id,
      iframeId: iframeId,
    });
    setTrackedEvents([...trackedEvents, event]);
  }

  function trackSubmissionEvent(submission) {
    if (!shouldTrackEvents) return;

    handleTrackEvent('submission', {
      customerEmail: submission.customerEmail,
      pageTitle: checkout.title,
      pageId: checkout._id,
    });
  }

  function trackPurchaseEvent(payment) {
    if (!shouldTrackEvents) return;

    if (checkout.type === PAGE_TYPES.CHECKOUT) {
      handleTrackEvent('checkout', {
        amount: payment.amount,
        currency: payment.currency,
        orderId: payment.orderId,
        customerEmail: payment.customer?.email,
        coupon: payment.coupon?.code,
        items: [
          {
            id: payment.product._id,
            name: payment.product.title,
            sku: payment.product.sku,
            price: payment.product.price,
          },
        ],
      });
    } else if (checkout.type === PAGE_TYPES.EVENT) {
      handleTrackEvent('checkout', {
        amount: payment.amount,
        currency: payment.currency,
        orderId: payment.orderId,
        customerEmail: payment.customer?.email,
        coupon: payment.coupon?.code,
        items: payment.tickets.map(ticket => ({
          id: ticket.ticketType,
          name: ticket.name,
          price: ticket.price * ticket.quantity,
        })),
      });
    }
  }

  function trackEnterEvent() {
    setShouldTrackEnterEvent(true);
  }

  useEffect(() => {
    if (!shouldTrackEnterEvent) return;

    if (!shouldTrackEvents) return;

    if (trackedEvents.includes('enter')) return;

    handleTrackEvent('enter');

    setShouldTrackEnterEvent(false);
  }, [shouldTrackEnterEvent, shouldTrackEvents, trackedEvents]);

  return (
    <EventTrackingContext.Provider
      value={{
        trackPurchaseEvent,
        trackSubmissionEvent,
        trackedEvents,
        setTrackedEvents,
        trackEvent: handleTrackEvent,
        trackEnterEvent,
      }}
    >
      {children}
    </EventTrackingContext.Provider>
  );
};

export default useEventTracking;
